import React, { useMemo } from 'react';
import moment from 'moment';

import { adaptDateToLocalTimezone } from '../../../../utils/converters/dateConverters';
import { VITALS_ACTIVITY_TYPE } from '../../constants';
import {
  convertValueDistanceToMiles,
  formatHeightValueWithMetric,
  formatTemperatureValueWithMetric,
  formatGlucoseValueWithMetric,
  formatValueWeightWithMetric,
  numberWithCommas,
  roundPrecision,
  getOriginalProviderCode,
} from '../../utils';
import { EMPTY_VITALS_CARD } from './cards';
import { useSafeDataSourceTitle } from '../../../common/Charts/hooks';

export function useActivityCardData({
  formatMessage,
  typeCard,
  data,
  isActiveCard,
  detailsMode,
  isWeightInKg,
  isHeightInMeters,
  isTemperatureInCelsius,
  mainUser,
  targetUserFirstName,
  isMilligramsPerDeciliter,
}) {
  const { getSafeDataSourceTitle } = useSafeDataSourceTitle(true);

  const {
    cardIcon,
    cardTitle,
    cardValue,
    cardMetric,
    cardDate,
    cardSource,
  } = useMemo(() => {
    switch (typeCard) {
      case VITALS_ACTIVITY_TYPE.STEPS:
        return {
          cardIcon: 'steps-black',
          cardTitle: formatMessage({ id: 'patient.healthboard.steps' }),
          cardValue:
            data && data.value
              ? numberWithCommas(data.value.baseValue.toFixed())
              : '-',
          cardMetric: formatMessage({ id: 'patient.healthboard.steps.metric' }),
          cardDate: data && data.measuredAt ? data.measuredAt : '',
          cardSource:
            data && data.originalProviderCodes
              ? getSafeDataSourceTitle(
                  getOriginalProviderCode(data.originalProviderCodes),
                  formatMessage,
                  mainUser,
                  targetUserFirstName,
                )
              : '',
        };
      case VITALS_ACTIVITY_TYPE.DISTANCE:
        return {
          cardIcon: 'distance-black',
          cardTitle: formatMessage({ id: 'patient.healthboard.distance' }),
          cardValue:
            data && data.value
              ? convertValueDistanceToMiles(data.value.baseValue)
              : '-',
          cardMetric: formatMessage({
            id: 'patient.healthboard.distance.metric',
          }),
          cardDate: data && data.measuredAt ? data.measuredAt : '',
          cardSource:
            data && data.originalProviderCodes
              ? getSafeDataSourceTitle(
                  getOriginalProviderCode(data.originalProviderCodes),
                  formatMessage,
                  mainUser,
                  targetUserFirstName,
                )
              : '',
        };
      case VITALS_ACTIVITY_TYPE.BMI:
        return {
          cardIcon: 'bmi-black',
          cardTitle: formatMessage({ id: 'patient.healthboard.bmi' }),
          cardValue: data && data.value ? data.value.baseValue.toFixed(1) : '-',
          cardMetric: '',
          cardDate: data && data.measuredAt ? data.measuredAt : '',
          cardSource:
            data && data.originalProviderCodes
              ? getSafeDataSourceTitle(
                  getOriginalProviderCode(data.originalProviderCodes),
                  formatMessage,
                  mainUser,
                  targetUserFirstName,
                )
              : '',
        };
      case VITALS_ACTIVITY_TYPE.WEIGHT:
        return {
          cardIcon: 'weight-black',
          cardTitle: formatMessage({ id: 'patient.healthboard.weight' }),
          cardValue:
            data && data.value
              ? formatValueWeightWithMetric(data.value.baseValue, isWeightInKg)
              : '-',
          cardMetric: isWeightInKg
            ? formatMessage({
                id: 'patient.healthboard.weight.metric.kg',
              })
            : formatMessage({
                id: 'patient.healthboard.weight.metric',
              }),
          cardDate: data && data.measuredAt ? data.measuredAt : '',
          cardSource:
            data && data.originalProviderCodes
              ? getSafeDataSourceTitle(
                  getOriginalProviderCode(data.originalProviderCodes),
                  formatMessage,
                  mainUser,
                  targetUserFirstName,
                )
              : '',
        };
      case VITALS_ACTIVITY_TYPE.HEIGHT:
        return {
          cardIcon: 'height-black',
          cardTitle: formatMessage({ id: 'patient.healthboard.height' }),
          cardValue:
            data && data.value
              ? formatHeightValueWithMetric(
                  data.value.baseValue,
                  isHeightInMeters,
                )
              : '-',
          cardMetric: isHeightInMeters
            ? formatMessage({
                id: 'patient.healthboard.height.metric.cm',
              })
            : '',
          cardDate: data && data.measuredAt ? data.measuredAt : '',
          cardSource:
            data && data.originalProviderCodes
              ? getSafeDataSourceTitle(
                  getOriginalProviderCode(data.originalProviderCodes),
                  formatMessage,
                  mainUser,
                  targetUserFirstName,
                )
              : '',
        };
      case VITALS_ACTIVITY_TYPE.SATURATION:
        return {
          cardIcon: 'icon-saturation-black',
          cardTitle: (
            <span>
              {formatMessage({ id: 'patient.healthboard.saturation' })}
              <sub>
                {formatMessage({ id: 'patient.healthboard.saturation.index' })}
              </sub>
              )
            </span>
          ),
          cardValue:
            data && data.value ? Math.round(data.value.baseValue) : '-',
          cardMetric: formatMessage({
            id: 'patient.healthboard.saturation.metric',
          }),
          cardDate: data && data.measuredAt ? data.measuredAt : '',
          cardSource:
            data && data.originalProviderCodes
              ? getSafeDataSourceTitle(
                  getOriginalProviderCode(data.originalProviderCodes),
                  formatMessage,
                  mainUser,
                  targetUserFirstName,
                )
              : '',
        };
      case VITALS_ACTIVITY_TYPE.HEART_RATE:
        return {
          cardIcon: 'icon-heart-rate-black',
          cardTitle: formatMessage({ id: 'patient.healthboard.heart_rate' }),
          cardValue:
            data && data.value ? Math.round(data.value.baseValue) : '-',
          cardMetric: formatMessage({
            id: 'patient.healthboard.heart_rate.metric',
          }),
          cardDate: data && data.measuredAt ? data.measuredAt : '',
          cardSource:
            data && data.originalProviderCodes
              ? getSafeDataSourceTitle(
                  getOriginalProviderCode(data.originalProviderCodes),
                  formatMessage,
                  mainUser,
                  targetUserFirstName,
                )
              : '',
        };
      case VITALS_ACTIVITY_TYPE.RESPIRATORY_RATE:
        return {
          cardIcon: 'icon-respiratory-rate',
          cardTitle: (
            <span>
              {formatMessage({
                id: 'patient.healthboard.respiratory_rate',
              })}
            </span>
          ),
          cardValue:
            data && data.value ? `${Math.round(data.value.baseValue)}` : '-',
          cardMetric: formatMessage({
            id: 'patient.healthboard.respiratory_rate.metric',
          }),
          cardDate: data && data.measuredAt ? data.measuredAt : '',
          cardSource:
            data && data.originalProviderCodes
              ? getSafeDataSourceTitle(
                  getOriginalProviderCode(data.originalProviderCodes),
                  formatMessage,
                  mainUser,
                  targetUserFirstName,
                )
              : '',
        };
      case VITALS_ACTIVITY_TYPE.BLOOD_PRESSURE:
        return {
          cardIcon: 'icon-blood-pressure-black',
          cardTitle: (
            <span>
              {formatMessage({
                id: 'patient.healthboard.blood_pressure',
              })}
            </span>
          ),
          cardValue:
            data && data.value
              ? `${Math.round(data.value.bpSystolic)}/${Math.round(
                  data.value.bpDiastolic,
                )}`
              : '-',
          cardMetric: formatMessage({
            id: 'patient.healthboard.blood_pressure.metric',
          }),
          cardDate: data && data.measuredAt ? data.measuredAt : '',
          cardSource:
            data && data.originalProviderCodes
              ? getSafeDataSourceTitle(
                  getOriginalProviderCode(data.originalProviderCodes),
                  formatMessage,
                  mainUser,
                  targetUserFirstName,
                )
              : '',
        };
      case VITALS_ACTIVITY_TYPE.BODY_SURFACE_AREA:
        return {
          cardIcon: 'bmi-black',
          cardTitle: formatMessage({ id: 'patient.healthboard.bsa' }),
          cardValue:
            data && data.value
              ? roundPrecision(data.value.baseValue, 1, true)
              : '-',
          cardMetric: (
            <span>
              {formatMessage({ id: 'patient.healthboard.bsa.metric' })}
              <sup>
                {formatMessage({ id: 'patient.healthboard.bsa.metricIndex' })}
              </sup>
            </span>
          ),
          cardDate: data && data.measuredAt ? data.measuredAt : '',
          cardSource:
            data && data.originalProviderCodes
              ? getSafeDataSourceTitle(
                  getOriginalProviderCode(data.originalProviderCodes),
                  formatMessage,
                  mainUser,
                  targetUserFirstName,
                )
              : '',
        };
      case VITALS_ACTIVITY_TYPE.BODY_TEMPERATURE:
        return {
          cardIcon: 'icon-body-temperature-black',
          cardTitle: formatMessage({
            id: 'patient.healthboard.body_temperature',
          }),
          cardValue:
            data && data.value
              ? formatTemperatureValueWithMetric(
                  data.value.baseValue,
                  isTemperatureInCelsius,
                )
              : '-',
          cardMetric: isTemperatureInCelsius
            ? formatMessage({
                id: 'patient.healthboard.body_temperature.metric.celsius',
              })
            : formatMessage({
                id: 'patient.healthboard.body_temperature.metric.fahrenheit',
              }),
          cardDate: data && data.measuredAt ? data.measuredAt : '',
          cardSource:
            data && data.originalProviderCodes
              ? getSafeDataSourceTitle(
                  getOriginalProviderCode(data.originalProviderCodes),
                  formatMessage,
                  mainUser,
                  targetUserFirstName,
                )
              : '',
        };
      case VITALS_ACTIVITY_TYPE.BLOOD_GLUCOSE:
        return {
          cardIcon: 'icon-blood-glucose-black',
          cardTitle: formatMessage({
            id: 'patient.healthboard.blood_glucose',
          }),
          cardValue:
            data && data.providerCode
              ? formatGlucoseValueWithMetric(
                  data.value.baseValue,
                  isMilligramsPerDeciliter,
                )
              : '-',
          cardMetric: isMilligramsPerDeciliter
            ? formatMessage({
                id:
                  'patient.healthboard.blood_glucose.metric.milligrams_per_deciliter',
              })
            : formatMessage({
                id:
                  'patient.healthboard.blood_glucose.metric.millimoles_per_liter',
              }),
          cardDate: data && data.measuredAt ? data.measuredAt : '',
          cardSource:
            data && data.originalProviderCodes
              ? getSafeDataSourceTitle(
                  getOriginalProviderCode(data.originalProviderCodes),
                  formatMessage,
                  mainUser,
                  targetUserFirstName,
                )
              : '',
        };
      case EMPTY_VITALS_CARD:
        return {
          cardIcon: '',
          cardTitle: '',
          cardValue: '',
          cardMetric: '',
          cardDate: '',
          cardSource: '',
        };
    }
  }, [
    isActiveCard,
    typeCard,
    data,
    detailsMode,
    formatMessage,
    isWeightInKg,
    isHeightInMeters,
    isTemperatureInCelsius,
  ]);

  const timeText = useMemo(() => {
    if (!cardDate) return '';
    const adapted = adaptDateToLocalTimezone(cardDate);
    const time = moment(adapted);

    const getFormat = () => {
      if (time.isSame(new Date(), 'day')) {
        return '[Today,] h:mm A';
      }
      if (time.isSame(new Date(), 'week')) {
        return 'MMM D, h:mm A';
      }
      if (time.isSame(new Date(), 'year')) {
        return 'MMM D, h:mm A';
      }
      return 'MMM D Y, h:mm A';
    };

    return time.format(getFormat());
  }, [cardDate]);

  return {
    cardIcon,
    cardTitle,
    cardValue,
    cardMetric,
    cardDate,
    timeText,
    cardSource,
  };
}



// WEBPACK FOOTER //
// ./src/components/PatientHealthboard/VitalsActivity/ActivityCard/useActivityCardData.jsx